import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import useIsMobile from '../hooks/useIsMobile';
import { DropZone } from './SheetBuilder';
import { api, apiFetch } from '../lib/api';
import EmailSearch from '../components/EmailSearch';

// Desktop sidebar nav (all pages)
const NAV = [
  { to: '/admin',          icon: 'fa-solid fa-house',         label: 'Home',     exact: true },
  { to: '/admin/sheets',   icon: 'fa-regular fa-clipboard',   label: 'Sheets'   },
  { to: '/admin/sheets/lab', icon: 'fa-solid fa-flask',       label: 'Sheet Builder (new)' },
  { to: '/admin/events',   icon: 'fa-regular fa-calendar',    label: 'Events'   },
  { to: '/admin/travel',   icon: 'fa-solid fa-plane',         label: 'Travel'   },
  { to: '/admin/artists',  icon: 'fa-solid fa-music',         label: 'Artists'  },
  { to: '/admin/people',   icon: 'fa-solid fa-users',         label: 'Users'    },
  { to: '/admin/distribution', icon: 'fa-solid fa-envelope',  label: 'Email Distribution' },
  { to: '/admin/approvals',    icon: 'fa-solid fa-circle-check', label: 'Approvals' },
  { to: '/admin/broadcasts',   icon: 'fa-solid fa-bullhorn',     label: 'Broadcasts' },
  { to: '/viewer',         icon: 'fa-solid fa-eye',           label: 'Viewer'   },
  { to: '/admin/settings', icon: 'fa-solid fa-gear',          label: 'Settings' },
];

// Mobile bottom tabs (subset)
const TABS = [
  { to: '/admin',          icon: 'fa-solid fa-house',       label: 'Today',    exact: true },
  { to: '/admin/sheets',   icon: 'fa-regular fa-clipboard', label: 'Sheets'   },
  // FAB goes in the center (rendered separately)
  { to: '/admin/artists',  icon: 'fa-solid fa-music',       label: 'Artists'  },
  { to: '/admin/settings', icon: 'fa-solid fa-gear',        label: 'Settings' },
];

const FAB_ACTIONS = [
  { icon: 'fa-regular fa-clipboard',  label: 'New Sheet',  to: '/admin/sheets/new' },
  { icon: 'fa-regular fa-calendar',   label: 'New Event',  to: '/admin/events?action=new' },
  { icon: 'fa-solid fa-plane',        label: 'Travel',     to: '/admin/travel?action=new' },
  { icon: 'fa-solid fa-file-import',  label: 'Import',     action: 'import' },
  { icon: 'fa-solid fa-envelope',     label: 'Invite',     action: 'invite' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteArtists, setInviteArtists] = useState([]);
  const [inviteSelIds, setInviteSelIds] = useState([]);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [showTransferPrompt, setShowTransferPrompt] = useState(false);
  const [transferArtists, setTransferArtists] = useState([]);
  const [connectingArtist, setConnectingArtist] = useState(null);
  const [approvalCount, setApprovalCount] = useState(0);

  // Email-verification banner state. Shown only to email signups whose
  // address hasn't been verified yet (Google signups skip this — they're
  // verified server-side at signup).
  const [resendingVerify, setResendingVerify] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const [hideVerifyBanner, setHideVerifyBanner] = useState(false);
  const handleResendVerify = async () => {
    setResendingVerify(true);
    try {
      await apiFetch('/auth/resend-verification', { method: 'POST', body: '{}' });
      setVerifySent(true);
    } catch { /* keep banner up so they can retry */ }
    finally { setResendingVerify(false); }
  };

  useEffect(() => {
    let alive = true;
    const load = () => apiFetch('/approvals/pending-count')
      .then(r => { if (alive) setApprovalCount(r?.count || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Check for ownership transfer prompt
  useEffect(() => {
    if (user?.isFirstUser && user?.ownerTransferPending) {
      apiFetch('/artists').then(res => {
        const list = res.artists || res || [];
        setTransferArtists(list);
        setShowTransferPrompt(true);
      }).catch(() => {});
    }
  }, [user]);

  const dismissTransferPrompt = async () => {
    setShowTransferPrompt(false);
    try { await apiFetch('/users/dismiss-transfer', { method: 'POST' }); } catch {}
  };

  const connectArtistCalendar = async (artistId) => {
    setConnectingArtist(artistId);
    try {
      const d = await apiFetch(`/calendar/connect/${artistId}`);
      if (d.url) window.location.href = d.url;
    } catch (e) { alert(e.message); setConnectingArtist(null); }
  };

  // Close FAB on navigation
  useEffect(() => { setFabOpen(false); }, [location.pathname]);

  const openInvite = async () => {
    setInviteOpen(true);
    setInviteEmail('');
    setInviteMsg('');
    try {
      const res = await apiFetch('/artists');
      const list = res.artists || res || [];
      setInviteArtists(list);
      if (list.length && inviteSelIds.length === 0) setInviteSelIds([list[0].id]);
    } catch {}
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteSelIds.length) return;
    setInviteSending(true);
    setInviteMsg('');
    try {
      const primaryId = inviteSelIds[0];
      const extraIds = inviteSelIds.slice(1);
      const res = await apiFetch(`/artists/${primaryId}/invite-batch`, {
        method: 'POST', body: JSON.stringify({ emails: [inviteEmail.trim()], artistIds: extraIds }),
      });
      const r = res.results?.[0];
      setInviteMsg(r?.status === 'sent' ? (r.isNewUser ? `Invite sent to ${inviteEmail}` : `${inviteEmail} now has access`) : `Error: ${r?.error || 'failed'}`);
      if (r?.status === 'sent') setInviteEmail('');
    } catch (e) {
      setInviteMsg('Error: ' + e.message);
    } finally {
      setInviteSending(false);
    }
  };

  const handleImportFiles = async (extracted, pdfData) => {
    if (!extracted.length) return;
    setImportSaving(true);
    setImportMsg('');
    try {
      // Get first artist as default
      const artistsRes = await apiFetch('/artists');
      const artists = artistsRes.artists || artistsRes || [];
      if (!artists.length) { setImportMsg('No artists found. Create an artist first.'); setImportSaving(false); return; }
      const artistId = artists[0].id;
      const today = new Date().toISOString().slice(0, 10);

      // Bulk create events
      const events = extracted.map(e => ({
        ...e,
        artistId,
        date: e.date || today,
      }));
      await apiFetch('/events/bulk', { method: 'POST', body: JSON.stringify({ events, artistId }) });
      setImportMsg(`${events.length} event${events.length !== 1 ? 's' : ''} imported!`);
      setTimeout(() => { setImportOpen(false); setImportMsg(''); }, 1500);
    } catch (e) {
      setImportMsg('Error: ' + e.message);
    } finally {
      setImportSaving(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const NavItems = ({ onClick }) => (
    <>
      {NAV.map(n => (
        <NavLink key={n.to} to={n.to} end={n.exact} onClick={onClick}
          style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 10,
            padding: collapsed ? '12px 0' : '12px 16px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: isActive ? 'var(--blue)' : 'var(--text2)',
            background: isActive ? 'rgba(79,156,249,0.1)' : 'transparent',
            borderLeft: isActive ? '3px solid var(--blue)' : '3px solid transparent',
            fontSize: 15, fontWeight: isActive ? 700 : 500,
            textDecoration: 'none', transition: 'all 0.15s',
          })}>
          <i className={n.icon} style={{ fontSize: 16, width: 20, textAlign: 'center' }}></i>
          {!collapsed && <span>{n.label}</span>}
        </NavLink>
      ))}
    </>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ═══ DESKTOP SIDEBAR ═══ */}
      {!isMobile && (
        <aside style={{
          width: collapsed ? 64 : 220,
          background: 'var(--bg2)',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          transition: 'width 0.2s ease',
          flexShrink: 0, position: 'sticky', top: 0, height: '100vh',
        }}>
          <div style={{ padding: collapsed ? '20px 0' : 20, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <img src="/icon.png" alt="" style={{ width: 32, height: 32, borderRadius: 8 }} />
            {!collapsed && <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>Day Sheets</span>}
          </div>
          <nav style={{ flex: 1, padding: '12px 0' }}>
            <NavItems />
          </nav>
          <div style={{ borderTop: '1px solid var(--border)', padding: collapsed ? '12px 0' : 16 }}>
            {!collapsed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 16, background: 'rgba(79,156,249,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'var(--blue)', overflow: 'hidden', flexShrink: 0 }}>
                  {user?.photo ? <img src={user.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (user?.firstName?.[0] || '?')}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{user?.firstName} {user?.lastName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'capitalize' }}>{user?.role}</div>
                </div>
              </div>
            )}
            <button onClick={() => {
              const current = document.documentElement.getAttribute('data-theme');
              const next = current === 'light' ? '' : 'light';
              document.documentElement.setAttribute('data-theme', next);
              localStorage.setItem('ds_theme', next);
            }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text3)', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', width: '100%', justifyContent: collapsed ? 'center' : 'flex-start' }}>
              <i className="fa-solid fa-circle-half-stroke" style={{ fontSize:14 }}></i>
              {!collapsed && <span>Theme</span>}
            </button>
            <button onClick={handleLogout}
              style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text2)', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', width: '100%', justifyContent: collapsed ? 'center' : 'flex-start' }}>
              <i className="fa-solid fa-arrow-right-from-bracket" style={{ fontSize:14 }}></i>
              {!collapsed && <span>Sign Out</span>}
            </button>
            <button onClick={() => setCollapsed(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text3)', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', width: '100%', justifyContent: collapsed ? 'center' : 'flex-start', marginTop: 4 }}>
              <span>{collapsed ? '→' : '←'}</span>
              {!collapsed && <span>Collapse</span>}
            </button>
          </div>
        </aside>
      )}

      {/* ═══ MOBILE TOP BAR ═══ */}
      {isMobile && (() => {
        const isSubPage = !['/admin', '/admin/sheets', '/admin/artists', '/admin/people', '/admin/settings'].includes(location.pathname);
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
            zIndex: 100,
          }}>
          <div style={{
            height: 48, paddingLeft: 14, paddingRight: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            {isSubPage ? (
              <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', color:'var(--blue)', fontSize:14, cursor:'pointer', fontFamily:'inherit', fontWeight:600, display:'flex', alignItems:'center', gap:4, padding:0 }}>
                <i className="fa-solid fa-chevron-left" style={{ fontSize:11 }}></i> Back
              </button>
            ) : <div />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, position:'absolute', left:'50%', transform:'translateX(-50%)' }}>
              <img src="/icon.png" alt="" style={{ width: 24, height: 24, borderRadius: 6 }} />
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.3 }}>Day Sheets</span>
            </div>
            <button onClick={() => navigate('/admin/approvals')}
              style={{ position:'relative', background:'none', border:'none', cursor:'pointer', color: approvalCount > 0 ? 'var(--blue)' : 'var(--text3)', fontSize:18, padding:4, display:'flex', alignItems:'center', justifyContent:'center' }}
              title={approvalCount > 0 ? `${approvalCount} awaiting your approval` : 'Approvals'}>
              <i className="fa-solid fa-circle-check"></i>
              {approvalCount > 0 && (
                <span style={{ position:'absolute', top:-2, right:-4, minWidth:17, height:17, borderRadius:9, background:'#ef4444', color:'#fff', fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', lineHeight:1, boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}>
                  {approvalCount > 9 ? '9+' : approvalCount}
                </span>
              )}
            </button>
          </div>
          </div>
        );
      })()}

      {/* ═══ MAIN CONTENT ═══ */}
      <main style={{
        flex: 1, overflow: 'auto', minWidth: 0,
        paddingTop: isMobile ? 'calc(48px + env(safe-area-inset-top, 0px))' : 0,
        paddingBottom: isMobile ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : 0,
      }}>
        {/* ── Email verification banner ── */}
        {user && user.emailVerified === false && !hideVerifyBanner && (
          <div style={{
            display:'flex', alignItems:'center', gap:12,
            padding:'10px 16px', margin:'12px 16px 0',
            background:'rgba(251,146,60,0.10)',
            border:'1px solid rgba(251,146,60,0.35)',
            borderRadius:12,
          }}>
            <i className="fa-solid fa-envelope-circle-check" style={{ fontSize:16, color:'#fb923c' }}></i>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>
                {verifySent ? 'Sent — check your email' : 'Verify your email'}
              </div>
              <div style={{ fontSize:11, color:'var(--text3)', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {verifySent ? 'It can take a minute to arrive.' : `We sent a link to ${user.email}.`}
              </div>
            </div>
            {!verifySent ? (
              <button onClick={handleResendVerify} disabled={resendingVerify}
                style={{ background:'#fb923c', border:'none', color:'#fff', padding:'6px 12px', borderRadius:999, fontSize:12, fontWeight:800, cursor: resendingVerify ? 'default' : 'pointer', fontFamily:'inherit' }}>
                {resendingVerify ? '…' : 'Resend'}
              </button>
            ) : (
              <button onClick={() => setHideVerifyBanner(true)}
                style={{ background:'none', border:'none', color:'var(--text3)', cursor:'pointer', padding:6, fontSize:14 }}>
                ×
              </button>
            )}
          </div>
        )}
        <Outlet />
      </main>

      {/* ═══ MOBILE FAB BACKDROP ═══ */}
      {isMobile && fabOpen && (
        <div onClick={() => setFabOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            transition: 'opacity 0.2s',
          }}
        />
      )}

      {/* ═══ MOBILE FAB ACTIONS ═══ */}
      {isMobile && fabOpen && (
        <div style={{
          position: 'fixed', bottom: 88, left: 20, right: 20,
          zIndex: 400, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10,
        }}>
          {FAB_ACTIONS.map((a, i) => (
            <button key={a.label}
              onClick={() => { setFabOpen(false); if (a.action === 'import') { setImportOpen(true); } else if (a.action === 'invite') { openInvite(); } else { navigate(a.to); } }}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 16, padding: '16px 20px',
                color: 'var(--text)', fontFamily: 'inherit', fontSize: 16, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                animation: `fabSlideUp 0.25s ease-out ${i * 0.05}s both`,
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'rgba(30,64,175,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className={a.icon} style={{ fontSize: 16, color: 'var(--blue)' }}></i>
              </div>
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ MOBILE BOTTOM TAB BAR ═══ */}
      {isMobile && (
        <>
          <style>{`
            @keyframes fabSlideUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <nav className="ds-mobile-tabbar" style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            paddingTop: 8, paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
            background: 'var(--bg2)',
            borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-around',
            zIndex: 200,
          }}>
            {/* First two tabs */}
            {TABS.slice(0, 2).map(t => {
              const isActive = t.exact
                ? location.pathname === t.to
                : location.pathname.startsWith(t.to);
              return (
                <NavLink key={t.to} to={t.to} end={t.exact}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                    textDecoration: 'none', padding: '6px 0', minWidth: 56,
                    color: isActive ? 'var(--blue)' : 'var(--text3)',
                    transition: 'color 0.15s',
                  }}>
                  <i className={t.icon} style={{ fontSize: 21 }}></i>
                  <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2 }}>{t.label}</span>
                </NavLink>
              );
            })}

            {/* Center FAB — background ring hides the border line */}
            <div style={{ position:'relative', display:'flex', alignItems:'center', justifyContent:'center', marginTop:-20 }}>
              <div style={{ position:'absolute', width:69, height:69, borderRadius:'50%', background:'var(--bg2)' }} />
              <button onClick={() => setFabOpen(v => !v)}
                style={{
                  position:'relative', zIndex:1,
                  width: 60, height: 60, borderRadius: '50%',
                  background: fabOpen
                    ? 'var(--card)'
                    : 'linear-gradient(135deg, #1E40AF, #1E40AF)',
                  border: fabOpen ? '1px solid var(--border)' : 'none',
                  color: fabOpen ? 'var(--text2)' : '#fff',
                  fontSize: 22, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: fabOpen ? 'none' : '0 4px 16px rgba(30,64,175,0.35)',
                  transition: 'all 0.2s ease',
                  transform: fabOpen ? 'rotate(45deg)' : 'none',
                }}>
                <i className="fa-solid fa-plus" style={{ fontSize: 23 }}></i>
              </button>
            </div>

            {/* Last two tabs */}
            {TABS.slice(2).map(t => {
              const isActive = location.pathname.startsWith(t.to);
              return (
                <NavLink key={t.to} to={t.to}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                    textDecoration: 'none', padding: '6px 0', minWidth: 56,
                    color: isActive ? 'var(--blue)' : 'var(--text3)',
                    transition: 'color 0.15s',
                  }}>
                  <i className={t.icon} style={{ fontSize: 21 }}></i>
                  <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2 }}>{t.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </>
      )}
      {/* ═══ OWNERSHIP TRANSFER PROMPT ═══ */}
      {showTransferPrompt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'var(--card)', borderRadius:20, padding:28, width:'100%', maxWidth:480, border:'1px solid var(--border)', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(251,191,36,0.12)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                <i className="fa-solid fa-crown" style={{ fontSize:24, color:'#fbbf24' }}></i>
              </div>
              <h2 style={{ fontSize:20, fontWeight:900, color:'var(--text)', margin:'0 0 6px' }}>Welcome, Owner</h2>
              <p style={{ fontSize:13, color:'var(--text3)', margin:0, lineHeight:1.5 }}>
                Ownership has been transferred to you. You can connect your Google Calendar to each artist for calendar sync.
              </p>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
              {transferArtists.map(a => {
                const hasCalendar = !!a.googleTokens;
                return (
                  <div key={a.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:12, background:'var(--bg)', border:'1px solid var(--border)' }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', overflow:'hidden', flexShrink:0, background: a.color || 'var(--blue)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> :
                        <span style={{ fontSize:14, fontWeight:800, color:'#fff' }}>{(a.name?.[0] || '?').toUpperCase()}</span>}
                    </div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>{a.name}</p>
                      <p style={{ fontSize:11, color: hasCalendar ? '#22c55e' : 'var(--text3)', margin:'2px 0 0' }}>
                        {hasCalendar ? <><i className="fa-solid fa-check-circle" style={{ marginRight:4, fontSize:9 }}></i>Calendar connected</> : 'No calendar connected'}
                      </p>
                    </div>
                    <button onClick={() => connectArtistCalendar(a.id)} disabled={connectingArtist === a.id}
                      style={{ padding:'8px 12px', borderRadius:8, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                        background: hasCalendar ? 'var(--bg)' : 'rgba(30,64,175,0.1)',
                        border: `1px solid ${hasCalendar ? 'var(--border)' : 'rgba(30,64,175,0.3)'}`,
                        color: hasCalendar ? 'var(--text3)' : 'var(--blue)' }}>
                      <i className="fa-brands fa-google" style={{ marginRight:4, fontSize:10 }}></i>
                      {hasCalendar ? 'Reconnect' : 'Connect'}
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <button onClick={dismissTransferPrompt}
                style={{ flex:1, padding:'14px', borderRadius:12, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Do it Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ IMPORT BOTTOM SHEET ═══ */}
      {importOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center', padding: isMobile ? 0 : 20 }}
          onClick={e => e.target === e.currentTarget && !importSaving && setImportOpen(false)}>
          <div style={{
            background:'var(--card)', borderRadius: isMobile ? '20px 20px 0 0' : 20, width:'100%',
            maxWidth: isMobile ? '100%' : 520, maxHeight:'92vh', overflowY:'auto',
            border:'1px solid var(--border)', borderBottom: isMobile ? 'none' : undefined,
            paddingBottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom))' : undefined,
          }}>
            {isMobile && <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'10px auto 0' }} />}
            <div style={{ padding:'16px 20px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', margin:0 }}>
                  <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight:8, color:'#f59e0b' }}></i>
                  Import Events
                </h3>
                <p style={{ fontSize:13, color:'var(--text3)', margin:'4px 0 0' }}>Drop a PDF, screenshot, or email to extract events</p>
              </div>
              <button onClick={() => !importSaving && setImportOpen(false)}
                style={{ width:32, height:32, borderRadius:16, background:'var(--bg)', border:'none', cursor:'pointer', fontSize:14, color:'var(--text3)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                ✕
              </button>
            </div>
            <div style={{ padding:'16px 20px 20px' }}>
              <DropZone onFiles={handleImportFiles} />
              {importMsg && (
                <div style={{ marginTop:12, padding:'10px 14px', borderRadius:10, fontSize:13, fontWeight:600,
                  background: importMsg.startsWith('Error') ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                  border: `1px solid ${importMsg.startsWith('Error') ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                  color: importMsg.startsWith('Error') ? '#ef4444' : '#22c55e' }}>
                  {importMsg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ INVITE BOTTOM SHEET ═══ */}
      {inviteOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center', padding: isMobile ? 0 : 20 }}
          onClick={e => e.target === e.currentTarget && !inviteSending && setInviteOpen(false)}>
          <div style={{
            background:'var(--card)', borderRadius: isMobile ? '20px 20px 0 0' : 20, width:'100%',
            maxWidth: isMobile ? '100%' : 440, maxHeight:'92vh', overflowY:'auto',
            border:'1px solid var(--border)', borderBottom: isMobile ? 'none' : undefined,
            paddingBottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom))' : undefined,
          }}>
            {isMobile && <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'10px auto 0' }} />}
            <div style={{ padding:'16px 20px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', margin:0 }}>
                <i className="fa-solid fa-envelope" style={{ marginRight:8, color:'var(--blue)' }}></i>
                Invite to Artist
              </h3>
              <button onClick={() => setInviteOpen(false)}
                style={{ width:32, height:32, borderRadius:16, background:'var(--bg)', border:'none', cursor:'pointer', fontSize:14, color:'var(--text3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                ✕
              </button>
            </div>
            <div style={{ padding:'16px 20px 20px', display:'flex', flexDirection:'column', gap:14 }}>
              <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>Send an invite email to join a team</p>
              {/* Artist selector — multi with photos */}
              {inviteArtists.length > 0 && (
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:6, textTransform:'uppercase' }}>Artists <span style={{ fontWeight:400, letterSpacing:0, textTransform:'none' }}>(tap multiple)</span></label>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {inviteArtists.map(a => {
                      const sel = inviteSelIds.includes(a.id);
                      return (
                        <button key={a.id} onClick={() => setInviteSelIds(prev => sel ? prev.filter(id => id !== a.id) : [...prev, a.id])}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:10, cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
                            background: sel ? 'rgba(30,64,175,0.08)' : 'var(--bg)',
                            border: `2px solid ${sel ? 'var(--blue)' : 'var(--border)'}`,
                            color: sel ? 'var(--text)' : 'var(--text3)' }}>
                          <div style={{ width:28, height:28, borderRadius:'50%', overflow:'hidden', background:`${a.color || 'var(--blue)'}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>
                            {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                          </div>
                          {a.name?.split(' ')[0]}
                          {sel && <i className="fa-solid fa-check" style={{ fontSize:10, color:'var(--blue)' }}></i>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Email input */}
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:6, textTransform:'uppercase' }}>Email Address</label>
                <EmailSearch value={inviteEmail} onChange={setInviteEmail} placeholder="Search contacts or type email..." />
              </div>
              {/* Send button */}
              {(() => {
                const ready = inviteEmail.trim().includes('@') && inviteSelIds.length > 0;
                return (
                  <button onClick={handleInvite} disabled={inviteSending || !ready}
                    style={{ width:'100%', padding:'14px', borderRadius:12, background: ready ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--bg)', border: ready ? 'none' : '1px solid var(--border)', color: ready ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor: ready ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    {inviteSending ? 'Sending...' : <><i className="fa-solid fa-paper-plane" style={{ fontSize:12 }}></i> Send Invite{inviteSelIds.length > 1 ? ` for ${inviteSelIds.length} Artists` : ''}</>}
                  </button>
                );
              })()}
              {inviteMsg && (
                <div style={{ padding:'10px 14px', borderRadius:10, fontSize:13, fontWeight:600,
                  background: inviteMsg.startsWith('Error') ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                  border: `1px solid ${inviteMsg.startsWith('Error') ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                  color: inviteMsg.startsWith('Error') ? '#ef4444' : '#22c55e' }}>
                  {inviteMsg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
